import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Compass, Download, FileText, Flag, Lightbulb, ListChecks, Lock,
  MessageCircle, RefreshCw, Save, ShieldAlert, Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PASTORAL_TEXT =
  "Take a moment to check in personally. This is a pastoral space — prayer requests, family, rest, joys, burdens. Nothing shared here is written down.";

const NOTES_REMINDER =
  "Document facts, agreements, and observable behavior — not diagnoses, speculation, or personal and spiritual matters.";

export default function MeetingRecord() {
  const { meetingId } = useParams();
  const { user, backendUrl } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [pairing, setPairing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [itemNotes, setItemNotes] = useState([]);
  const [generalNotes, setGeneralNotes] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [saving, setSaving] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(null); // { kind: 'notes'|'general'|null, index?: number }
  const [aiInput, setAiInput] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Auto-save orchestration: dirty flag flips on any note change; a
  // debounced timer flushes to the same meeting_id (idempotent PUT — never
  // creates a duplicate draft).
  const dirtyRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const latestSnapshotRef = useRef({ itemNotes: [], generalNotes: "" });

  const load = async () => {
    try {
      const m = await api.get(`/rhythm/meetings/${meetingId}`);
      setMeeting(m.data);
      setItemNotes(m.data.item_notes || []);
      setGeneralNotes(m.data.general_notes || "");
      const p = await api.get(`/rhythm/pairings/${m.data.pairing_id}`);
      setPairing(p.data);
      const s = await api.get(`/rhythm/settings`);
      setSettings(s.data);
      const sp = await api.get(`/rhythm/pairings/${m.data.pairing_id}/suggest-prompts?count=2`);
      setSuggested(sp.data);
      // Activity feed — audit rows for this meeting record.
      try {
        const act = await api.get(`/rhythm/meetings/${meetingId}/activity`);
        setActivity(act.data.events || []);
      } catch (actErr) {
        // pastor_metadata / employee-on-draft see 403 — expected.
        if (actErr?.response?.status !== 403) {
          console.error("meeting activity load failed", actErr);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);

  const viewerRole = pairing?.viewer_role;
  const isSupervisor = viewerRole === "supervisor";
  const isEmployee = viewerRole === "employee";
  const isOversight = viewerRole === "oversight";
  const isPastorMeta = viewerRole === "pastor_metadata";
  const isParticipant = isSupervisor || isEmployee;
  const isDraft = meeting?.status === "in_progress";
  const isFinalized = meeting?.status === "finalized";
  const isAcknowledged = meeting?.status === "acknowledged";
  const isLocked = meeting?.locked;
  // Only the SUPERVISOR of record can edit/finalize drafts. PO/Super Admin
  // viewing an OTHER pairing sees oversight-read-only.
  const canEditDraft = isDraft && isSupervisor;
  const canFinalize = isDraft && isSupervisor;
  const canAcknowledge = isFinalized && isEmployee;
  // Addenda: participants only (supervisor or employee of record).
  const canAddendum = (isFinalized || isAcknowledged) && isParticipant;
  // Direct-feedback flag: supervisor of record only.
  const canFlag = (isFinalized || isAcknowledged) && isSupervisor && meeting && !meeting.direct_feedback_flag;

  const saveDraft = async ({ silent } = {}) => {
    if (!canEditDraft) return;
    setSaving(true);
    try {
      await api.put(`/rhythm/meetings/${meetingId}`, {
        item_notes: latestSnapshotRef.current.itemNotes,
        general_notes: latestSnapshotRef.current.generalNotes,
      });
      dirtyRef.current = false;
      setLastSavedAt(new Date());
      if (!silent) toast.success("Draft saved.");
      await load();
    } catch (err) {
      if (!silent) toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  // Track latest state so the debounced timer can flush without stale closures.
  useEffect(() => {
    latestSnapshotRef.current = { itemNotes, generalNotes };
  }, [itemNotes, generalNotes]);

  // 30-second auto-save. Only runs when the draft is dirty AND editable.
  useEffect(() => {
    if (!canEditDraft) return undefined;
    dirtyRef.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (dirtyRef.current) saveDraft({ silent: true });
    }, 30000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemNotes, generalNotes, canEditDraft]);

  // Flush pending edits when tab is hidden (killed-tab recovery: same meeting_id
  // means the next reload rehydrates the exact single draft — no duplicates).
  useEffect(() => {
    if (!canEditDraft) return undefined;
    const onHide = () => {
      if (dirtyRef.current) saveDraft({ silent: true });
    };
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    return () => {
      window.removeEventListener("beforeunload", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditDraft]);

  if (!meeting || !pairing) {
    return <p className="italic text-charcoal-muted p-6">Loading meeting…</p>;
  }

  const setItemNote = (idx, value) => {
    setItemNotes((prev) => prev.map((it, i) => (i === idx ? { ...it, notes: value } : it)));
  };

  const openAI = (kind, index) => {
    setAiOpen({ kind, index });
    setAiInput("");
    setAiResult(null);
  };

  const runAI = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      // Attribute correctly by name. We're the AUTHOR; the OTHER party in
      // the pairing is the COUNTERPART. Send the model our respective
      // roles on this pairing so behavior is attributed to the right person.
      const authorRole = isSupervisor ? "supervisor" : "employee";
      const counterRole = isSupervisor ? "employee" : "supervisor";
      const counterName = isSupervisor ? pairing.employee_name : pairing.supervisor_name;
      const { data } = await api.post("/rhythm/ai/draft", {
        mode: "meeting_notes",
        input_text: aiInput,
        pairing_context: {
          author_name: user?.name || "",
          author_role_in_pairing: authorRole,
          counterpart_name: counterName || "",
          counterpart_role_in_pairing: counterRole,
          record_type: "1on1_meeting_notes",
        },
      });
      setAiResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "AI unavailable right now.");
    } finally { setAiLoading(false); }
  };

  const useAIDraft = () => {
    if (!aiResult) return;
    if (aiOpen.kind === "general") setGeneralNotes(aiResult.draft);
    if (aiOpen.kind === "notes") setItemNote(aiOpen.index, aiResult.draft);
    setAiOpen(null); setAiResult(null); setAiInput("");
  };

  const downloadPDF = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/rhythm/meetings/${meetingId}/export.pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Unable to fetch");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rhythm-meeting-${meetingId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Couldn't export this meeting."); }
  };

  return (
    <div className="space-y-6" data-testid="rhythm-meeting-page">
      <div>
        <Link to={`/rhythm/workspace/${pairing.id}`} className="text-sm text-forest-700 hover:text-forest-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To Workspace
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap mt-2">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
              1:1 Meeting Record
            </p>
            <h1 className="text-4xl font-serif text-forest-900">
              {pairing.supervisor_name} + {pairing.employee_name}
            </h1>
            <p className="text-sm text-charcoal-soft mt-2">
              {(meeting.finalized_at || meeting.started_at || "").slice(0, 10)}
              &nbsp;· <span className="capitalize">{meeting.status.replace("_", " ")}</span>
              {meeting.compass_check_in && (
                <span className="inline-flex items-center gap-1 ml-2 text-terracotta-600">
                  <Compass className="h-3.5 w-3.5" strokeWidth={1.5} /> COMPASS Check-In
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isLocked && (
              <span className="patria-chip-green inline-flex items-center gap-1" data-testid="record-locked-chip">
                <Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Locked
              </span>
            )}
            <button onClick={downloadPDF} className="patria-btn-secondary text-sm" data-testid="btn-export-meeting-pdf">
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {isOversight && (
        <section className="patria-card p-4 bg-forest-900/5 border-l-4 border-forest-700 text-sm text-charcoal-soft" data-testid="meeting-oversight-notice">
          You are viewing this record as People Operations — <strong>read-only oversight</strong>.
          Only participants can add addenda or flag direct feedback. This access is audit-logged.
        </section>
      )}
      {isPastorMeta && (
        <section className="patria-card p-4 bg-terracotta-50 border-l-4 border-terracotta-500 text-sm text-charcoal-soft" data-testid="meeting-pastor-notice">
          You are viewing this pairing as a Campus Pastor — metadata only. Note content is not
          shown unless you are the supervisor of record.
        </section>
      )}
      {meeting.hidden_draft && (
        <section className="patria-card p-6 bg-cream-100 border-l-4 border-terracotta-500" data-testid="hidden-draft-notice">
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
            1:1 In Progress
          </p>
          <p className="text-forest-900 mt-1 font-medium">
            {(meeting.started_at || "").slice(0, 10)}
          </p>
          <p className="text-sm text-charcoal-soft mt-1">
            This 1:1 is still a draft. Content becomes visible when the supervisor
            finalizes the record.
          </p>
        </section>
      )}

      {/* Two-Track pastoral prompt — no persistence anywhere */}
      {!meeting.hidden_draft && (
      <section className="patria-card p-5 bg-terracotta-500/10 border-l-4 border-terracotta-500" data-testid="pastoral-prompt">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-terracotta-500/20 text-terracotta-700 flex items-center justify-center">
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta-600 font-semibold">
              Personal & Pastoral Check-In
            </p>
            <p className="text-sm text-charcoal-soft mt-1">
              {settings?.microcopy?.pastoral_prompt_text || PASTORAL_TEXT}
            </p>
            <p className="mt-2 text-xs italic text-charcoal-muted">
              Nothing shared here is written down or persisted anywhere in MyPatria.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Suggested prompts */}
      {isDraft && isSupervisor && suggested.length > 0 && (
        <section className="patria-card p-5" data-testid="suggested-prompts">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-forest-900">Suggested Prompts</h3>
            <button
              className="ml-auto patria-btn-ghost text-sm"
              onClick={async () => {
                const { data } = await api.get(`/rhythm/pairings/${pairing.id}/suggest-prompts?count=2`);
                setSuggested(data);
              }}
              data-testid="btn-shuffle-prompts"
            >
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /> Shuffle
            </button>
          </div>
          <ul className="mt-3 space-y-2">
            {suggested.map((p) => (
              <li key={p.id} className="text-sm text-forest-900">
                <span className="text-[10px] uppercase tracking-widest text-charcoal-muted mr-2">
                  {p.category.replaceAll("_", " ")}
                </span>
                &ldquo;{p.text}&rdquo;
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Item notes */}
      {!meeting.hidden_draft && (
      <section className="patria-card p-6" data-testid="meeting-item-notes">
        <h3 className="font-serif text-2xl text-forest-900">Agenda Item Notes</h3>
        <p className="text-xs italic text-charcoal-muted mt-1">
          {settings?.microcopy?.notes_field_reminder || NOTES_REMINDER}
        </p>
        <ul className="mt-4 space-y-4">
          {itemNotes.length === 0 && <li className="text-sm text-charcoal-muted italic">(No items were on the agenda.)</li>}
          {itemNotes.map((it, i) => (
            <li key={it.id || `agenda-${it.title}-${i}`} className="p-3 rounded-xl bg-cream-100">
              <p className="font-medium text-forest-900">{it.title}</p>
              <textarea
                className="patria-input w-full mt-2 min-h-[100px]"
                value={it.notes || ""}
                onChange={(e) => setItemNote(i, e.target.value)}
                readOnly={!canEditDraft}
                placeholder={canEditDraft ? "What did you discuss? What was agreed?" : ""}
                data-testid={`item-note-${i}`}
              />
              {canEditDraft && (
                <button
                  type="button"
                  onClick={() => openAI("notes", i)}
                  className="patria-btn-ghost mt-2 text-sm"
                  data-testid={`btn-ai-item-${i}`}
                >
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Draft With AI
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      )}

      {/* General notes */}
      {!meeting.hidden_draft && (
      <section className="patria-card p-6" data-testid="meeting-general-notes">
        <h3 className="font-serif text-2xl text-forest-900">General Notes</h3>
        <textarea
          className="patria-input w-full mt-3 min-h-[140px]"
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          readOnly={!canEditDraft}
          placeholder={canEditDraft ? "What else did you cover?" : ""}
          data-testid="general-notes-input"
        />
        {canEditDraft && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => openAI("general", null)}
              className="patria-btn-ghost text-sm min-h-[44px]"
              data-testid="btn-ai-general"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Draft With AI
            </button>
            <button
              type="button"
              onClick={() => setExtractOpen(true)}
              className="patria-btn-ghost text-sm min-h-[44px]"
              data-testid="btn-extract-commitments"
            >
              <ListChecks className="h-3.5 w-3.5" strokeWidth={1.5} /> Extract Commitments
            </button>
            <button
              onClick={() => saveDraft({ silent: false })}
              disabled={saving}
              className="patria-btn-primary min-h-[44px]"
              data-testid="btn-save-draft"
            >
              <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {saving ? "Saving…" : "Save Draft"}
            </button>
            <span className="text-xs italic text-charcoal-muted" data-testid="autosave-indicator">
              {saving
                ? "Saving…"
                : lastSavedAt
                ? `Draft auto-saved · ${lastSavedAt.toLocaleTimeString()}`
                : "Auto-save every 30 seconds"}
            </span>
          </div>
        )}
      </section>
      )}

      {/* Action buttons */}
      {!meeting.hidden_draft && (
      <section className="patria-card p-6 flex flex-wrap items-center gap-3">
        {canFinalize && (
          <button
            className="patria-btn-primary"
            onClick={() => setFinalizeOpen(true)}
            data-testid="btn-open-finalize"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Finalize This Record
          </button>
        )}
        {canAcknowledge && (
          <button
            className="patria-btn-primary"
            onClick={() => setAckOpen(true)}
            data-testid="btn-open-ack"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Acknowledge Record
          </button>
        )}
        {(isFinalized || isAcknowledged) && canAddendum && (
          <button
            className="patria-btn-secondary"
            onClick={() => setAddendumOpen(true)}
            data-testid="btn-open-addendum"
          >
            <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add Addendum
          </button>
        )}
        {(isFinalized || isAcknowledged) && canFlag && (
          <button
            className="patria-btn-secondary"
            onClick={() => setFlagOpen(true)}
            data-testid="btn-open-flag"
          >
            <Flag className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Flag Direct Feedback
          </button>
        )}
      </section>
      )}

      {/* Activity feed (collapsed by default; every event on this record) */}
      {!meeting.hidden_draft && activity.length > 0 && (
        <section className="patria-card p-6" data-testid="activity-section">
          <button
            type="button"
            className="w-full flex items-center justify-between text-left"
            onClick={() => setActivityOpen((v) => !v)}
            data-testid="btn-toggle-activity"
          >
            <div>
              <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                Audit Trail
              </p>
              <p className="font-serif text-xl text-forest-900">Activity On This Record</p>
              <p className="text-xs text-charcoal-muted">
                {activity.length} event{activity.length === 1 ? "" : "s"} recorded.
              </p>
            </div>
            <span className="text-sm text-forest-700">
              {activityOpen ? "Hide" : "Show"}
            </span>
          </button>
          {activityOpen && (
            <ul className="mt-4 space-y-2" data-testid="activity-list">
              {activity.map((e) => (
                <li key={e.id} className="p-3 rounded-xl bg-cream-100">
                  <p className="text-xs text-charcoal-muted">
                    {(e.timestamp || "").slice(0, 19).replace("T", " ")} · {e.actor_name || "system"}
                  </p>
                  <p className="text-forest-900 text-sm">
                    {(e.action || "").replaceAll("rhythm_", "").replaceAll("_", " ")}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Direct feedback flag summary */}
      {meeting.direct_feedback_flag && (
        <section className="patria-card p-5 border-l-4 border-terracotta-500" data-testid="direct-feedback-chip">
          <p className="text-xs uppercase tracking-widest text-terracotta-600 font-semibold">
            Direct Feedback Flagged
          </p>
          <p className="mt-1 text-sm text-charcoal-soft">
            {meeting.direct_feedback_flag.topic_note || "(no topic note)"} · Tags: {(meeting.direct_feedback_flag.tags || []).join(", ")}
          </p>
        </section>
      )}

      {/* Addenda list */}
      {(meeting.addenda || []).length > 0 && (
        <section className="patria-card p-6" data-testid="addenda-list">
          <h3 className="font-serif text-2xl text-forest-900">Addenda</h3>
          <ul className="mt-3 space-y-3">
            {meeting.addenda.map((a) => (
              <li key={a.id} className="p-3 rounded-xl bg-cream-100">
                <p className="text-xs text-charcoal-muted">
                  {(a.at || "").slice(0, 19).replace("T", " ")} · {a.by_name}
                </p>
                <p className="text-forest-900 mt-1 whitespace-pre-wrap">{a.text}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {meeting.acknowledgment_comment && (
        <section className="patria-card p-5 bg-forest-900/5" data-testid="ack-comment">
          <p className="text-xs uppercase tracking-widest text-forest-700 font-semibold">Employee Acknowledgment Comment</p>
          <p className="text-sm text-charcoal-soft mt-1 whitespace-pre-wrap">{meeting.acknowledgment_comment}</p>
        </section>
      )}

      <FinalizeModal open={finalizeOpen} onClose={() => setFinalizeOpen(false)} onDone={load} meetingId={meetingId} />
      <AckModal open={ackOpen} onClose={() => setAckOpen(false)} onDone={load} meetingId={meetingId} />
      <AddendumModal open={addendumOpen} onClose={() => setAddendumOpen(false)} onDone={load} meetingId={meetingId} />
      <FlagModal open={flagOpen} onClose={() => setFlagOpen(false)} onDone={load} meetingId={meetingId} />
      <AIModal
        open={!!aiOpen}
        onClose={() => setAiOpen(null)}
        input={aiInput}
        setInput={setAiInput}
        result={aiResult}
        loading={aiLoading}
        onRun={runAI}
        onUse={useAIDraft}
      />
      <ExtractCommitmentsModal
        open={extractOpen}
        onClose={() => setExtractOpen(false)}
        onDone={load}
        meetingId={meetingId}
        pairing={pairing}
      />
    </div>
  );
}

function ExtractCommitmentsModal({ open, onClose, onDone, meetingId, pairing }) {
  const [proposals, setProposals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);
  const [errorReason, setErrorReason] = useState(null);

  useEffect(() => {
    if (!open) return;
    setProposals(null); setSelected({}); setErrorReason(null);
    setLoading(true);
    api.post(`/rhythm/meetings/${meetingId}/extract-commitments`, {})
      .then((r) => {
        setProposals(r.data.proposals || []);
        setErrorReason(r.data.reason || null);
        // Default: all proposals selected — user can uncheck what shouldn't fire.
        const initial = {};
        (r.data.proposals || []).forEach((_, i) => { initial[i] = true; });
        setSelected(initial);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't extract commitments.");
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meetingId]);

  const accept = async () => {
    if (!proposals) return;
    setCreating(true);
    let created = 0;
    let failed = 0;
    for (let i = 0; i < proposals.length; i += 1) {
      if (!selected[i]) continue;
      const p = proposals[i];
      try {
        // Owner defaults to the pairing employee unless the proposal names the supervisor
        const supName = (pairing.supervisor_name || "").toLowerCase();
        const ownerId = (p.owner_hint || "").toLowerCase().includes(supName)
          ? pairing.supervisor_id
          : pairing.employee_id;
        await api.post(`/rhythm/pairings/${pairing.id}/action-items`, {
          title: p.title,
          owner_id: ownerId,
          due_date: p.due_date || null,
          meeting_id: meetingId,
        });
        created += 1;
      } catch {
        failed += 1;
      }
    }
    setCreating(false);
    if (created > 0) toast.success(`${created} action item${created === 1 ? "" : "s"} added. Review + edit in the workspace.`);
    if (failed > 0) toast.error(`${failed} proposal${failed === 1 ? "" : "s"} couldn't be created — try again from the workspace.`);
    onDone();
    onClose();
  };

  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Extract Commitments" testId="extract-modal">
      <p className="text-sm text-charcoal-soft">
        We scan <strong>only</strong> what you and your teammate agreed to in the notes on this
        record. Nothing is auto-created — approve each proposal below.
      </p>
      <p className="text-[10px] italic text-charcoal-muted mt-1">
        Extract Commitments is action-only. Pastoral or personal moments are never proposed as
        action items. This is extraction, not evaluation.
      </p>

      {loading && <p className="mt-4 italic text-charcoal-muted">Reading the notes…</p>}

      {!loading && errorReason === "no_notes" && (
        <p className="mt-4 text-sm text-charcoal-soft" data-testid="extract-no-notes">
          No note content to extract from yet. Add your discussion notes and try again.
        </p>
      )}

      {!loading && proposals && proposals.length === 0 && !errorReason && (
        <p className="mt-4 text-sm text-charcoal-soft" data-testid="extract-none-proposed">
          Nothing rose to the level of a commitment. Keep the notes as they are, or add
          action items directly from the workspace.
        </p>
      )}

      {!loading && proposals && proposals.length > 0 && (
        <>
          <ul className="mt-4 space-y-2" data-testid="extract-proposals">
            {proposals.map((p, i) => (
              <li
                key={`${p.title}-${i}`}
                className="p-3 rounded-xl bg-cream-100 flex items-start gap-3"
                data-testid={`extract-proposal-${i}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[i]}
                  onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                  className="mt-1 h-5 w-5"
                  data-testid={`extract-check-${i}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-forest-900">{p.title}</p>
                  {p.owner_hint && (
                    <p className="text-xs text-charcoal-muted">Owner suggested: {p.owner_hint}</p>
                  )}
                  {p.due_date && (
                    <p className="text-xs text-charcoal-muted">Due suggested: {p.due_date}</p>
                  )}
                  {p.evidence_quote && (
                    <p className="mt-1 text-xs italic text-charcoal-soft border-l-2 border-forest-600 pl-2">
                      &ldquo;{p.evidence_quote}&rdquo;
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="patria-btn-ghost min-h-[44px]"
              data-testid="btn-extract-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={accept}
              disabled={creating}
              className="patria-btn-primary min-h-[44px]"
              data-testid="btn-extract-accept"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              {creating ? "Creating…" : "Approve Selected"}
            </button>
          </div>
        </>
      )}
    </Overlay>
  );
}

// ----- Modals -----
function FinalizeModal({ open, onClose, onDone, meetingId }) {
  const [typedName, setTypedName] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rhythm/meetings/${meetingId}/finalize`, {
        typed_name: typedName, consent: true,
      });
      toast.success("Record finalized. Your teammate has been asked to acknowledge.");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Finalize This Record" testId="finalize-modal">
      <p className="text-sm text-charcoal-soft">
        Once finalized, this record is sent to your teammate for acknowledgment. After they acknowledge, the record locks — any corrections happen as addenda.
      </p>
      <input
        className="patria-input w-full mt-4"
        placeholder="Type your name to confirm"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
        data-testid="finalize-typed-name"
      />
      <button onClick={submit} disabled={!typedName.trim()} className="patria-btn-primary w-full mt-4" data-testid="btn-finalize-confirm">
        Finalize
      </button>
    </Overlay>
  );
}

function AckModal({ open, onClose, onDone, meetingId }) {
  const [typedName, setTypedName] = useState("");
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rhythm/meetings/${meetingId}/acknowledge`, {
        typed_name: typedName, consent: true, comment,
      });
      toast.success("Thank you. The record is now locked in.");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Acknowledge This Record" testId="ack-modal">
      <p className="text-sm text-charcoal-soft">
        Read the record carefully. You can leave a comment that becomes part of the permanent record.
      </p>
      <textarea
        className="patria-input w-full mt-4 min-h-[100px]"
        placeholder="Comment (optional but appreciated)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        data-testid="ack-comment-input"
      />
      <input
        className="patria-input w-full mt-3"
        placeholder="Type your name to confirm"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
        data-testid="ack-typed-name"
      />
      <button onClick={submit} disabled={!typedName.trim()} className="patria-btn-primary w-full mt-4" data-testid="btn-ack-confirm">
        Acknowledge
      </button>
    </Overlay>
  );
}

function AddendumModal({ open, onClose, onDone, meetingId }) {
  const [text, setText] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rhythm/meetings/${meetingId}/addenda`, { text });
      toast.success("Addendum added.");
      onDone(); onClose(); setText("");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Add An Addendum" testId="addendum-modal">
      <p className="text-sm text-charcoal-soft">
        The original record can&rsquo;t be edited. Add a dated correction or clarification below.
      </p>
      <textarea
        className="patria-input w-full mt-4 min-h-[120px]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="addendum-text"
      />
      <button onClick={submit} disabled={!text.trim()} className="patria-btn-primary w-full mt-4" data-testid="btn-addendum-confirm">
        Save Addendum
      </button>
    </Overlay>
  );
}

function FlagModal({ open, onClose, onDone, meetingId }) {
  const [tags, setTags] = useState("");
  const [topic, setTopic] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rhythm/meetings/${meetingId}/direct-feedback`, {
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        topic_note: topic,
      });
      toast.success("Flagged as direct feedback.");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Flag Direct Feedback Given" testId="flag-modal">
      <p className="text-sm text-charcoal-soft">
        Flag this record as evidence of direct feedback given on a specific topic. This surfaces on the COMPASS entry-review screen.
      </p>
      <input
        className="patria-input w-full mt-4"
        placeholder="Tags (comma-separated) e.g. communication, reliability"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        data-testid="flag-tags"
      />
      <input
        className="patria-input w-full mt-3"
        placeholder="Short topic note"
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        data-testid="flag-topic"
      />
      <button onClick={submit} disabled={!tags.trim()} className="patria-btn-primary w-full mt-4" data-testid="btn-flag-confirm">
        Flag Direct Feedback
      </button>
    </Overlay>
  );
}

function AIModal({ open, onClose, input, setInput, result, loading, onRun, onUse }) {
  if (!open) return null;
  return (
    <Overlay onClose={onClose} title="Draft With AI" testId="ai-modal">
      <p className="text-sm text-charcoal-soft">
        Type rough bullets or half-sentences. The draft expands only what you wrote — never adds facts, never speculates.
      </p>
      <textarea
        className="patria-input w-full mt-3 min-h-[100px]"
        placeholder="- Talked about volunteer pipeline&#10;- Agreed to draft 30/60/90 rhythm"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        data-testid="ai-input"
      />
      <button onClick={onRun} disabled={loading || !input.trim()} className="patria-btn-primary mt-3" data-testid="btn-ai-run">
        <Sparkles className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        {loading ? "Drafting…" : "Draft With AI"}
      </button>
      {result && (
        <div className="mt-4 p-3 rounded-xl bg-cream-100" data-testid="ai-result">
          {result.stripped_pastoral && (
            <div className="text-xs text-terracotta-700 mb-2" data-testid="ai-two-track-notice">
              <ShieldAlert className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              {result.two_track_notice}
            </div>
          )}
          <pre className="whitespace-pre-wrap text-sm text-forest-900">{result.draft}</pre>
          <button onClick={onUse} className="patria-btn-primary mt-3" data-testid="btn-ai-use">
            Use This Draft
          </button>
        </div>
      )}
    </Overlay>
  );
}

function Overlay({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid={testId}>
      <div className="absolute inset-0 bg-forest-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-cream-100 rounded-2xl shadow-card p-6 animate-fade-up">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-forest-900">{title}</h3>
          <button onClick={onClose} className="patria-btn-ghost">Close</button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
